// The llama's socket to its room. It says hello with this browser's token each
// time it opens, and opens again by itself after a drop. A phone drops the
// socket of a tab in the background, so a drop is normal and the page only
// says it is reconnecting. Modelled on arcanechess's roomSocket.ts.

import {
  type LlamaMsg,
  PING_EVERY_MS,
  PING_TEXT,
  PROTOCOL_VERSION,
  type ToAny,
  type ToLlama,
} from '../protocol';
import type { Link } from './state';

const RECONNECT_FIRST_MS = 500;
const RECONNECT_MOST_MS = 8000;

// The room ends things for one of these. The socket does not open again.
const FINAL: ReadonlySet<string> = new Set([
  'kicked',
  'full',
  'gone',
  'old',
  'closed',
  'room_locked',
  'cleared',
]);

export interface LlamaSocket {
  send: (msg: LlamaMsg) => void;
  close: () => void;
}

const socketUrl = (code: string): string => {
  const url = new URL(`/api/rooms/${code}/socket`, window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.href;
};

// `name` rides each hello. Empty keeps the name the room has for the token.
export function openLlamaSocket(
  code: string,
  token: string,
  name: string,
  onMessage: (msg: ToLlama | ToAny) => void,
  onLink: (link: Link) => void,
): LlamaSocket {
  let socket: WebSocket | null = null;
  let ended = false;
  let delay = RECONNECT_FIRST_MS;
  let timer = 0;

  const connect = (): void => {
    if (ended || socket) return;
    window.clearTimeout(timer);
    const next = new WebSocket(socketUrl(code));
    socket = next;
    next.onopen = () => {
      delay = RECONNECT_FIRST_MS;
      const hello: LlamaMsg = {
        t: 'hello',
        role: 'llama',
        token,
        protocol: PROTOCOL_VERSION,
        ...(name ? { name } : {}),
      };
      next.send(JSON.stringify(hello));
      if (document.visibilityState !== 'visible') next.send(JSON.stringify({ t: 'away' }));
      onLink('open');
    };
    next.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let msg: ToLlama | ToAny;
      try {
        msg = JSON.parse(event.data) as ToLlama | ToAny;
      } catch {
        return;
      }
      if (FINAL.has(msg.t)) ended = true;
      onMessage(msg);
    };
    next.onclose = () => {
      if (socket === next) socket = null;
      if (ended) {
        onLink('closed');
        return;
      }
      onLink('reconnecting');
      timer = window.setTimeout(connect, delay);
      delay = Math.min(delay * 2, RECONNECT_MOST_MS);
    };
  };

  const sendNow = (text: string): void => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(text);
  };

  // Presence (protocol.ts): a ping each PING_EVERY_MS while the page is on
  // screen. The room answers it without waking.
  const pinger = window.setInterval(() => {
    if (document.visibilityState === 'visible') sendNow(PING_TEXT);
  }, PING_EVERY_MS);

  // A page sent to the background says so at once, so the room stops waiting
  // on it and the game shows its llama asleep. Back on screen it says so,
  // and a socket that dropped meanwhile tries again at once.
  const onVisibility = (): void => {
    if (ended) return;
    if (document.visibilityState !== 'visible') {
      sendNow(JSON.stringify({ t: 'away' }));
      return;
    }
    sendNow(JSON.stringify({ t: 'here' }));
    sendNow(PING_TEXT);
    tryNow();
  };
  const onPageHide = (): void => sendNow(JSON.stringify({ t: 'away' }));

  // A tab or a network that comes back tries at once.
  const tryNow = (): void => {
    if (document.visibilityState !== 'visible' || ended) return;
    delay = RECONNECT_FIRST_MS;
    connect();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('online', tryNow);
  onLink('connecting');
  connect();

  return {
    // A guess sent while the socket is down is dropped. The page shows the pad
    // again, and the llama sends it once the socket is back.
    send: (msg) => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
    },
    close: () => {
      ended = true;
      window.clearTimeout(timer);
      window.clearInterval(pinger);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('online', tryNow);
      socket?.close(1000, 'closed');
      socket = null;
      onLink('closed');
    },
  };
}
